import { TimeRange } from '@mi-front/core';
import { PropsWithChildren, useContext, useLayoutEffect, useMemo } from 'react';
import { PlaybackStoreContext } from '../context/playback.context';
import { PlaybackProvider } from '../context/PlaybackProvider';
import { ExclusivePlaybackMode } from '../store/base-playback-slice';
import { createVideoPlaybackStore } from './useVideoPlaybackStore';

export type VideoPlaybackProviderProps = {
  /** Optional source URL for media */
  src?: string;
  /** Optional playback range for bounded playback (e.g., fragment chunks) */
  playbackRange?: TimeRange<number>;
  exclusivePlaybackMode?: ExclusivePlaybackMode;
  /** Optional identifier for this playback instance, shown in debug logs */
  playbackId?: string;
  /** When true, start playback once the Video.js player is ready. */
  autoPlay?: boolean;
};

/**
 * Provider component that creates a video playback store and passes it to PlaybackProvider.
 * Used for video playback with optional source URL and playback range.
 */
export function VideoJsPlaybackProvider({
  src,
  playbackRange,
  children,
  exclusivePlaybackMode = 'pause',
  playbackId,
  autoPlay = false,
}: PropsWithChildren<VideoPlaybackProviderProps>) {
  // Create store exactly once per component instance
  const videoStore = useMemo(
    () =>
      createVideoPlaybackStore({
        src,
        playbackRange,
        exclusivePlaybackMode,
        playbackId,
        autoPlay,
      }),
    [],
  );

  // When nested inside another playback context (e.g. logger stream), register this store with the parent
  const ctx = useContext(PlaybackStoreContext)!;

  // useLayoutEffect (not useEffect): promote store before paint so nested media does not
  // collapse to height 0 for a frame while waiting on the parent shell.
  useLayoutEffect(() => {
    ctx?.setStore(videoStore);
  }, []);

  // Root usage: no parent context — provide our own PlaybackProvider
  if (!ctx) {
    return <PlaybackProvider store={videoStore}>{children}</PlaybackProvider>;
  }

  if (ctx.store === videoStore) {
    return children;
  }

  // Parent shell exists but has no store yet (e.g. FragmentMediaDetail). Bridge immediately so
  // the player mounts in the same frame; layout effect promotes the store for siblings.
  if (!ctx.store) {
    return (
      <PlaybackStoreContext.Provider value={{ store: videoStore, setStore: ctx.setStore }}>
        {children}
      </PlaybackStoreContext.Provider>
    );
  }

  // Nested usage: only render children after parent context points to this exact store
  // to avoid mounting video consumers while an audio store is still active.
  return null;
}
